import {MeasurementTarget, MeasurementTargetRecord} from '@domain/materials'

import {
  getDayPeriodForMeasurementTypeName,
  getMeasurementTypeNameForDayPeriod
} from '../constants/measurementTypes'
import {DayPeriod, StrengthTarget} from '../types'

import {GlobalMeasurementType} from '@settings/modules/measurements/types/GlobalMeasurementType'

export function mapGlobalMeasurementIdToDayPeriod(
  globalMeasurementId: number,
  globalMeasurementTypes: GlobalMeasurementType[]
): DayPeriod | null {
  const measurementType = globalMeasurementTypes.find((type) => type.id === globalMeasurementId)

  if (!measurementType) {
    return null
  }

  return getDayPeriodForMeasurementTypeName(measurementType.name)
}

function transformMeasurementTargetRecord(
  record: MeasurementTargetRecord & {
    createdByName?: string
    createdByInitials?: string
    createdAt?: string
    modifiedByName?: string
    modifiedByInitials?: string
    modifiedAt?: string
  },
  dayPeriod: DayPeriod,
  index: number
): StrengthTarget | null {
  if (!record.from) {
    console.error('Invalid measurement target record: missing "from" date', record)
    return null
  }

  return {
    id: `${dayPeriod}-${index}`,
    dayPeriod,
    target: record.target,
    lowerBound: record.lowerBound,
    upperBound: record.upperBound,
    validFrom: record.from,
    validTo: record.to,
    comment: record.comment,
    isCurrent: false
  }
}

export function transformMeasurementTargetsToStrengthTargets(
  measurementTargets: MeasurementTarget[] | undefined,
  globalMeasurementTypes: GlobalMeasurementType[]
): StrengthTarget[] {
  if (!measurementTargets || measurementTargets.length === 0) {
    return []
  }

  return measurementTargets.flatMap((measurementTarget) => {
    const dayPeriod = mapGlobalMeasurementIdToDayPeriod(
      measurementTarget.globalMeasurementId,
      globalMeasurementTypes
    )

    if (!dayPeriod) {
      return []
    }

    return measurementTarget.measurementTargetRecords
      .map((record, index) => transformMeasurementTargetRecord(record, dayPeriod, index))
      .filter((target): target is StrengthTarget => target !== null)
  })
}

export function transformStrengthTargetToMeasurementTargetRecord(
  target: StrengthTarget
): MeasurementTargetRecord {
  return {
    from: target.validFrom ? new Date(target.validFrom).toISOString() : undefined,
    to: target.validTo ? new Date(target.validTo).toISOString() : undefined,
    target: target.target,
    lowerBound: target.lowerBound,
    upperBound: target.upperBound,
    comment: target.comment
  }
}

export function mapDayPeriodToGlobalMeasurementId(
  dayPeriod: DayPeriod,
  globalMeasurementTypes: GlobalMeasurementType[]
): number | null {
  const measurementTypeName = getMeasurementTypeNameForDayPeriod(dayPeriod)
  const measurementType = globalMeasurementTypes.find((mt) => mt.name === measurementTypeName)
  return measurementType?.id ?? null
}

export function transformStrengthTargetsToMeasurementTargets(
  strengthTargets: StrengthTarget[],
  globalMeasurementTypes: GlobalMeasurementType[]
): MeasurementTarget[] {
  const targetsByPeriod: Record<DayPeriod, StrengthTarget[]> = {
    1: [],
    2: [],
    3: [],
    7: [],
    28: []
  }

  strengthTargets.forEach((target) => {
    targetsByPeriod[target.dayPeriod].push(target)
  })

  return Object.entries(targetsByPeriod).flatMap(([dayPeriodStr, targets]) => {
    if (targets.length === 0) return []

    const dayPeriod = Number(dayPeriodStr) as DayPeriod
    const globalMeasurementId = mapDayPeriodToGlobalMeasurementId(dayPeriod, globalMeasurementTypes)

    if (!globalMeasurementId) {
      console.warn(`Cannot find globalMeasurementId for day period ${dayPeriod}`)
      return []
    }

    return {
      globalMeasurementId,
      measurementTargetRecords: targets.map((target) =>
        transformStrengthTargetToMeasurementTargetRecord(target)
      )
    }
  })
}
